package fr.manyverse.app.presentation.viewmodel

import androidx.lifecycle.viewModelScope
import fr.manyverse.app.domain.model.ActiveAnnouncement
import fr.manyverse.app.domain.model.DieConfig
import fr.manyverse.app.domain.model.DieType
import fr.manyverse.app.domain.model.EventContent
import fr.manyverse.app.domain.model.EventType
import fr.manyverse.app.domain.model.GameEvent
import fr.manyverse.app.domain.model.ImageData
import fr.manyverse.app.domain.usecase.CancelAnnouncementUseCase
import fr.manyverse.app.domain.usecase.CreateAnnouncementUseCase
import fr.manyverse.app.domain.usecase.CreateDiceRollEventUseCase
import fr.manyverse.app.domain.usecase.DeleteGameTableUseCase
import fr.manyverse.app.domain.usecase.GetTableEventsUseCase
import fr.manyverse.app.domain.usecase.KickPlayerUseCase
import fr.manyverse.app.domain.usecase.ObserveRealtimeReconnectionsUseCase
import fr.manyverse.app.domain.usecase.ObserveTableEventsUseCase
import fr.manyverse.app.domain.usecase.SendGmAlertUseCase
import fr.manyverse.app.domain.usecase.SendMessageUseCase
import fr.manyverse.app.domain.usecase.UpdateGameTableNameUseCase
import fr.manyverse.app.infrastructure.datastore.StoredDiceConfig
import fr.manyverse.app.infrastructure.datastore.UserPreferencesDataStore
import fr.manyverse.app.presentation.components.notification.NotificationContent
import fr.manyverse.app.presentation.error.UserErrorNotifier
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.gamemaster.GameMasterAnnouncementsDelegate
import fr.manyverse.app.presentation.viewmodel.gamemaster.GameMasterEventsDelegate
import fr.manyverse.app.presentation.viewmodel.gamemaster.GameMasterMessagesDelegate
import fr.manyverse.app.presentation.viewmodel.gamemaster.GameMasterNotificationsDelegate
import kotlin.time.Clock
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

private val LAST_SEEN_PERSIST_INTERVAL = 5.seconds
private val EXCLUDED_FROM_HISTORY = setOf(EventType.ANNOUNCEMENT_CANCELLED)

/**
 * Coordinates the Game Master screen. The bulk of the state is owned by focused delegates
 * ([GameMasterEventsDelegate], [GameMasterMessagesDelegate], [GameMasterAnnouncementsDelegate],
 * [GameMasterNotificationsDelegate]); this ViewModel wires them together, owns the single
 * Realtime subscription that fans events out to them, and keeps the table-level actions
 * (kick, delete, dice config, GM alerts).
 */
class GameMasterViewModel(
    private val tableId: String,
    private val currentUserId: String,
    private val availableDice: List<DieConfig>,
    createAnnouncementUseCase: CreateAnnouncementUseCase,
    cancelAnnouncementUseCase: CancelAnnouncementUseCase,
    private val createDiceRollEventUseCase: CreateDiceRollEventUseCase,
    getTableEventsUseCase: GetTableEventsUseCase,
    private val observeTableEventsUseCase: ObserveTableEventsUseCase,
    private val sendMessageUseCase: SendMessageUseCase,
    private val kickPlayerUseCase: KickPlayerUseCase,
    private val deleteGameTableUseCase: DeleteGameTableUseCase,
    private val updateGameTableNameUseCase: UpdateGameTableNameUseCase,
    private val sendGmAlertUseCase: SendGmAlertUseCase,
    observeRealtimeReconnectionsUseCase: ObserveRealtimeReconnectionsUseCase,
    private val userPreferencesDataStore: UserPreferencesDataStore,
    private val errorNotifier: UserErrorNotifier
) : BaseViewModel() {

    /** Retours du temps réel après une coupure : l'écran y relance la même resynchronisation qu'au retour au premier plan. */
    val reconnections: Flow<Unit> = observeRealtimeReconnectionsUseCase()

    private val eventsDelegate = GameMasterEventsDelegate(
        scope = viewModelScope,
        tableId = tableId,
        getTableEventsUseCase = getTableEventsUseCase,
        excludedFromHistory = EXCLUDED_FROM_HISTORY
    )

    private val messagesDelegate = GameMasterMessagesDelegate(
        scope = viewModelScope,
        tableId = tableId,
        currentUserId = currentUserId,
        getTableEventsUseCase = getTableEventsUseCase
    )

    private val announcementsDelegate = GameMasterAnnouncementsDelegate(
        scope = viewModelScope,
        tableId = tableId,
        currentUserId = currentUserId,
        createAnnouncementUseCase = createAnnouncementUseCase,
        cancelAnnouncementUseCase = cancelAnnouncementUseCase,
        getTableEventsUseCase = getTableEventsUseCase,
        userPreferencesDataStore = userPreferencesDataStore,
        errorNotifier = errorNotifier,
        onAnnouncementCreated = { eventsDelegate.loadEvents() }
    )

    private val notificationsDelegate = GameMasterNotificationsDelegate(
        scope = viewModelScope,
        tableId = tableId,
        currentUserId = currentUserId,
        availableDice = availableDice,
        getTableEventsUseCase = getTableEventsUseCase,
        userPreferencesDataStore = userPreferencesDataStore,
        persistLastSeen = ::persistLastSeen
    )

    // --- Feed -------------------------------------------------------------------------------
    val eventsState: StateFlow<AsyncUiState<List<GameEvent>>> get() = eventsDelegate.eventsState
    val events: StateFlow<List<GameEvent>> get() = eventsDelegate.events
    val loadMoreEventsState: StateFlow<AsyncUiState<Unit>> get() = eventsDelegate.loadMoreEventsState
    val hasMoreEvents: StateFlow<Boolean> get() = eventsDelegate.hasMoreEvents

    // --- Messages ---------------------------------------------------------------------------
    val messages: StateFlow<List<GameEvent>> get() = messagesDelegate.messages
    val hasMoreOlderMessages: StateFlow<Boolean> get() = messagesDelegate.hasMoreOlderMessages
    val loadMoreMessagesState: StateFlow<AsyncUiState<Unit>> get() = messagesDelegate.loadMoreMessagesState
    val unreadMessageCount: StateFlow<Int> get() = messagesDelegate.unreadMessageCount

    // --- Announcements ----------------------------------------------------------------------
    val announcementState: StateFlow<AsyncUiState<GameEvent>> get() = announcementsDelegate.announcementState
    val activeAnnouncements: StateFlow<List<ActiveAnnouncement>> get() = announcementsDelegate.activeAnnouncements

    // --- Notifications ----------------------------------------------------------------------
    val notifications: StateFlow<List<NotificationContent>> get() = notificationsDelegate.notifications

    // --- Table-level actions ----------------------------------------------------------------
    private val _kickState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val kickState: StateFlow<AsyncUiState<Unit>> = _kickState.asStateFlow()

    private val _deleteTableState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val deleteTableState: StateFlow<AsyncUiState<Unit>> = _deleteTableState.asStateFlow()

    private val _renameTableState = MutableStateFlow<AsyncUiState<Unit>>(AsyncUiState.Idle)
    val renameTableState: StateFlow<AsyncUiState<Unit>> = _renameTableState.asStateFlow()

    private val _activePlayerAlert = MutableStateFlow<EventContent.PlayerAlert?>(null)
    val activePlayerAlert: StateFlow<EventContent.PlayerAlert?> = _activePlayerAlert.asStateFlow()

    private val _savedDiceConfig = MutableStateFlow<AsyncUiState<StoredDiceConfig?>>(AsyncUiState.Loading)
    val savedDiceConfig: StateFlow<AsyncUiState<StoredDiceConfig?>> = _savedDiceConfig.asStateFlow()

    private var observeEventsJob: Job? = null
    private var lastForegroundRefreshAt: Instant = Instant.DISTANT_PAST
    private var lastRealtimeEventAt: Instant = Instant.DISTANT_PAST
    private var lastSeenPersistedAt: Instant = Instant.DISTANT_PAST

    init {
        announcementsDelegate.loadDismissedAnnouncements()
        eventsDelegate.loadEvents()
        messagesDelegate.loadMessages()
        announcementsDelegate.load()
        notificationsDelegate.loadInitialNotifications()
        startObservingEvents()
        viewModelScope.launch {
            _savedDiceConfig.value =
                AsyncUiState.Success(userPreferencesDataStore.getDiceConfig(currentUserId, tableId))
        }
    }

    fun saveDiceConfig(dieType: DieType, count: Int, modifier: Int) {
        // Mise à jour immédiate de l'état : l'onglet dés est détruit dès qu'on le quitte et se
        // reconstruit à partir de ce flow. Sans ça, il repartirait de la config chargée à l'init.
        _savedDiceConfig.value = AsyncUiState.Success(StoredDiceConfig(dieType.name, count, modifier))
        viewModelScope.launch {
            userPreferencesDataStore.saveDiceConfig(currentUserId, tableId, dieType.name, count, modifier)
        }
    }

    /**
     * Persiste le timestamp "dernier event vu" pour cet utilisateur sur cette table,
     * avec un throttle pour éviter une écriture DataStore par event Realtime.
     */
    private fun persistLastSeen(now: Instant) {
        if (now - lastSeenPersistedAt < LAST_SEEN_PERSIST_INTERVAL) return
        lastSeenPersistedAt = now
        viewModelScope.launch {
            userPreferencesDataStore.saveLastSeenAt(
                userId = currentUserId,
                tableId = tableId,
                epochMillis = now.toEpochMilliseconds()
            )
        }
    }

    private fun startObservingEvents() {
        observeEventsJob?.cancel()
        observeEventsJob = viewModelScope.launch {
            observeTableEventsUseCase(tableId).collect { newEvent ->
                val now = Clock.System.now()
                lastRealtimeEventAt = now
                persistLastSeen(now)
                eventsDelegate.onRealtimeEvent(newEvent)
                when (newEvent.eventType) {
                    EventType.DICE_ROLLED -> notificationsDelegate.onRealtimeEvent(newEvent)
                    EventType.MESSAGE_SENT -> {
                        messagesDelegate.onRealtimeMessage(newEvent)
                        notificationsDelegate.onRealtimeEvent(newEvent)
                    }
                    EventType.PLAYER_JOINED -> notificationsDelegate.onRealtimeEvent(newEvent)
                    EventType.PLAYER_ALERT ->
                        _activePlayerAlert.value = newEvent.content as? EventContent.PlayerAlert
                    EventType.ANNOUNCEMENT -> announcementsDelegate.onRealtimeAnnouncement(newEvent)
                    EventType.ANNOUNCEMENT_CANCELLED -> announcementsDelegate.onRealtimeCancelled(newEvent)
                    EventType.GM_ALERT -> Unit
                    else -> Unit
                }
            }
        }
    }

    fun dismissPlayerAlert() {
        _activePlayerAlert.value = null
    }

    fun loadEvents() = eventsDelegate.loadEvents()

    fun loadMoreEvents() = eventsDelegate.loadMoreEvents()

    fun loadMessages() = messagesDelegate.loadMessages()

    fun loadOlderMessages() = messagesDelegate.loadOlderMessages()

    /**
     * @param afterReconnection après une coupure du temps réel, les events manqués ne seront pas
     *   rejoués : on recharge même si un event a été reçu juste avant la coupure.
     */
    fun refreshOnForeground(afterReconnection: Boolean = false) {
        val now = Clock.System.now()
        // Debounce 2s : ON_RESUME peut être déclenché plusieurs fois (config change, multi-window).
        if (now - lastForegroundRefreshAt < 2.seconds && !afterReconnection) return
        lastForegroundRefreshAt = now
        // Si un event Realtime a été reçu récemment, la subscription tient les flux à jour
        // et un fetch HTTP est redondant.
        if (now - lastRealtimeEventAt < 30.seconds && !afterReconnection) return
        eventsDelegate.refreshEvents()
        messagesDelegate.loadMessages()
        announcementsDelegate.load()
    }

    fun createAnnouncement(
        text: String?,
        image: ImageData?,
        targetParticipants: List<String>
    ) = announcementsDelegate.create(text, image, targetParticipants)

    fun cancelAnnouncement(announcementEventId: String) =
        announcementsDelegate.cancel(announcementEventId)

    fun createDiceRollEvent(
        characterName: String,
        dieType: DieType,
        count: Int,
        results: List<Int>,
        isGmRoll: Boolean,
        modifier: Int = 0
    ) {
        viewModelScope.launch {
            createDiceRollEventUseCase(
                tableId = tableId,
                triggeredBy = currentUserId,
                characterName = characterName,
                dieType = dieType,
                count = count,
                results = results,
                isGmRoll = isGmRoll,
                modifier = modifier
            ).onFailure(errorNotifier::notify)
        }
    }

    fun sendMessage(text: String, recipientIds: List<String>, senderName: String) {
        viewModelScope.launch {
            sendMessageUseCase(
                tableId = tableId,
                senderId = currentUserId,
                senderName = senderName,
                recipientIds = recipientIds,
                text = text
            ).onFailure(errorNotifier::notify)
        }
    }

    fun sendGmAlert(message: String?) {
        viewModelScope.launch {
            sendGmAlertUseCase(tableId = tableId, gmId = currentUserId, message = message)
                .onFailure(errorNotifier::notify)
        }
    }

    fun kickPlayer(userId: String) {
        launchAction(_kickState) { kickPlayerUseCase(tableId, userId, currentUserId) }
    }

    fun resetKickState() {
        resetState(_kickState)
    }

    fun deleteTable(playerUserIds: List<String>) {
        launchAction(_deleteTableState) {
            deleteGameTableUseCase(tableId, currentUserId, playerUserIds)
        }
    }

    fun resetDeleteTableState() {
        resetState(_deleteTableState)
    }

    fun renameTable(name: String) {
        launchAction(_renameTableState) { updateGameTableNameUseCase(tableId, name) }
    }

    fun resetRenameTableState() {
        resetState(_renameTableState)
    }

    fun clearUnreadMessages() = messagesDelegate.clearUnreadMessages()

    fun dismissNotification(id: String) = notificationsDelegate.dismissNotification(id)

    fun markAllNotificationsSeen() = notificationsDelegate.markAllNotificationsSeen()

    override fun onCleared() {
        super.onCleared()
        observeEventsJob?.cancel()
    }
}
